import { StandardEvents } from '@shopify/events';

class BundleProgressBar extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';

    this.header = document.querySelector(
      'archetype-custom-header'
    );

    this.message = this.querySelector(
      '[data-bundle-progress-message]'
    );

    this.status = this.querySelector(
      '[data-bundle-progress-status]'
    );

    this.count = Number(
      this.dataset.initialCount || 0
    );

    this.isHomepage =
      this.header?.dataset.homepage === 'true';

    this.onCartUpdate =
      this.onCartUpdate.bind(this);

    this.onLegacyCartUpdate =
      this.onLegacyCartUpdate.bind(this);

    this.syncState =
      this.syncState.bind(this);

    this.requestSync =
      this.requestSync.bind(this);

    this.syncFrame = 0;

    this.pageWrapper =
      document.querySelector('.page-wrapper');

    this.headerSection =
      this.header?.closest(
        '[id^="shopify-section-"]'
      );

    this.onPageShow =
      this.onPageShow.bind(this);

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    document.addEventListener(
      'cart:updated',
      this.onLegacyCartUpdate
    );

    window.addEventListener(
      'resize',
      this.requestSync
    );

    window.addEventListener(
      'scroll',
      this.requestSync,
      {
        passive: true,
      }
    );

    this.pageWrapper?.addEventListener(
      'scroll',
      this.requestSync,
      {
        passive: true,
      }
    );

    window.addEventListener(
      'pageshow',
      this.onPageShow
    );

if (this.header) {
  /*
   * Observe only the header's state classes.
   * The callback is throttled to one execution
   * per animation frame.
   */
  this.headerObserver =
    new MutationObserver(
      this.requestSync
    );

  this.headerObserver.observe(
    this.header,
    {
      attributes: true,
      attributeFilter: ['class'],
    }
  );
}

    this.render(this.count);
  }

  disconnectedCallback() {
    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    document.removeEventListener(
      'cart:updated',
      this.onLegacyCartUpdate
    );

    window.removeEventListener(
      'resize',
      this.requestSync
    );

    window.removeEventListener(
      'scroll',
      this.requestSync
    );

    this.pageWrapper?.removeEventListener(
      'scroll',
      this.requestSync
    );

    window.removeEventListener(
      'pageshow',
      this.onPageShow
    );

    this.headerObserver?.disconnect();

    if (this.syncFrame) {
      window.cancelAnimationFrame(
        this.syncFrame
      );

      this.syncFrame = 0;
    }
  }

  getVisibleItemCount(items = []) {
    return items.reduce((total, item) => {
      /*
       * Do not count the technical zero-price
       * Mix & Match parent product.
       */
      if (
        item?.properties?._mmb_role === 'parent'
      ) {
        return total;
      }

      return (
        total + Number(item?.quantity || 0)
      );
    }, 0);
  }

  onCartUpdate(event) {
    event.promise
      ?.then(({ cart, detail }) => {
        if (detail?.didError) return;

        if (Array.isArray(detail?.items)) {
          this.render(
            this.getVisibleItemCount(
              detail.items
            )
          );

          return;
        }

        if (
          Number.isFinite(
            cart?.totalQuantity
          )
        ) {
          this.render(cart.totalQuantity);
          return;
        }

        this.refreshCart();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          this.refreshCart();
        }
      });
  }

  onLegacyCartUpdate(event) {
    const cart = event.detail?.cart;

    if (Array.isArray(cart?.items)) {
      this.render(
        this.getVisibleItemCount(
          cart.items
        )
      );
    } else {
      this.refreshCart();
    }
  }

  onPageShow(event) {
    if (event.persisted) {
      this.refreshCart();
    }
  }

  async refreshCart() {
    try {
      const root =
        window.Shopify?.routes?.root || '/';

      const response = await fetch(
        `${root}cart.js`,
        {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) return;

      const cart = await response.json();

      this.render(
        this.getVisibleItemCount(
          cart.items || []
        )
      );
    } catch (error) {
      console.warn(
        '[bundle-progress-bar] Cart refresh failed.',
        error
      );
    }
  }

  render(count) {
    this.count = Math.max(
      0,
      Number(count) || 0
    );

    let message = '';
    let accessibleStatus = '';
    let tier = 0;
    let progress = 0;

if (this.count === 1) {
  message =
    'Add 1 more item to unlock 10% off';

  accessibleStatus =
    'One item added. Add one more item to unlock 10% off.';

  tier = 1;
  progress = 16.666;
} else if (this.count === 2) {
  message =
    'Add 1 more item for 15% off';

  accessibleStatus =
    'Add one more item to unlock 15% off.';

  tier = 2;
  progress = 50;
} else if (this.count === 3) {
  message =
    'Add 1 more item for 20% off';

  accessibleStatus =
    'Add one more item to unlock 20% off.';

  tier = 3;
  progress = 83.333;
} else if (this.count >= 4) {
  message =
    'You unlocked 20% off your bundle';

  accessibleStatus =
    'Maximum 20% bundle discount unlocked.';

  tier = 4;
  progress = 100;
}

    this.dataset.count =
      String(this.count);

    this.dataset.tier =
      String(tier);

    this.style.setProperty(
      '--bundle-progress-value',
      `${progress}%`
    );

    if (this.message) {
      this.message.textContent = message;
    }

    if (this.status) {
      this.status.textContent =
        accessibleStatus;
    }

    this.syncState();
  }
  requestSync() {
    if (this.syncFrame) return;

    this.syncFrame =
      window.requestAnimationFrame(() => {
        this.syncFrame = 0;
        this.syncState();
      });
  }
  syncState() {
    const hasItems = this.count > 0;

    const headerRect =
      this.header?.getBoundingClientRect();

    const headerIsSticky =
      this.header?.classList.contains(
        'is-sticky'
      );

    const homepageAtTop =
      this.isHomepage &&
      this.header?.classList.contains(
        'is-at-top'
      ) &&
      !headerIsSticky;

    const shouldShow =
      hasItems && !homepageAtTop;

    /*
    * Reserve progress-bar space on secondary pages.
    * This class is placed on the section, not on the
    * observed header element.
    */
    this.headerSection?.classList.toggle(
      'has-bundle-progress',
      shouldShow && !this.isHomepage
    );

    if (this.hidden === shouldShow) {
      this.hidden = !shouldShow;
    }

    const nextAriaHidden =
      String(!shouldShow);

    if (
      this.getAttribute('aria-hidden') !==
      nextAriaHidden
    ) {
      this.setAttribute(
        'aria-hidden',
        nextAriaHidden
      );
    }

    if (!shouldShow) {
      this.removeAttribute(
        'data-position'
      );

      this.removeAttribute(
        'data-below-header'
      );

      this.headerSection?.classList.remove(
        'is-bundle-progress-attached'
      );

      return;
    }

    let position = 'attached';

    if (headerIsSticky) {
      position = 'below-header';
    } else if (
      this.isHomepage &&
      headerRect &&
      headerRect.bottom <= 0
    ) {
      position = 'viewport-top';
    }

    /*
    * Avoid unnecessary attribute mutations
    * during every scroll frame.
    */
    if (
      this.dataset.position !== position
    ) {
      this.dataset.position = position;
    }

    const isAttachedToHeader =
      position === 'attached' ||
      position === 'below-header';

    if (
      this.hasAttribute(
        'data-below-header'
      ) !== isAttachedToHeader
    ) {
      this.toggleAttribute(
        'data-below-header',
        isAttachedToHeader
      );
    }

    /*
    * Apply the visual connected state to the
    * unobserved section instead of the header.
    */
    this.headerSection?.classList.toggle(
      'is-bundle-progress-attached',
      isAttachedToHeader
    );
  }
}

if (
  !customElements.get(
    'bundle-progress-bar'
  )
) {
  customElements.define(
    'bundle-progress-bar',
    BundleProgressBar
  );
}